import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkedinIconInMailCompassDark from 'crono-fe-common/icons/Icon-Linkedin-Inmail-Only-Compass-Dark';
import PlusSmallIcon from 'crono-fe-common/icons/Icon-Plus-Small';
import { colors } from 'crono-fe-common/theme';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import { StepTypeOptionsWrapper, StrategyStepWrapper } from './style';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { dispartPascalCase } from 'utils/formatUtils';
import {
  mapTaskTypeToTemplateType,
  parseTaskTypeToString,
} from 'utils/businessLogic';
import ReplyThreadIcon from 'crono-fe-common/icons/Icon-Reply-Thread';
import AddTemplateIconS from 'crono-fe-common/icons/Icon-Add-Template-S';
import SelectedTemplateIcon from 'crono-fe-common/icons/Icon-Selected-Template';
import EditPencilIconS from 'crono-fe-common/icons/Icon-Edit-Pencil-S';
import { CustomOptionsTooltip } from 'pages/strategy/customOptionsTooltip';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { Template } from 'crono-fe-common/types/template';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import { StrategyConditionalStepPatch } from 'crono-fe-common/types/DTO/strategyConditionalStepPatch';
import ConditionalStepDetailComponent from '../conditionalStepDetailComponent';
import PlusIcon from 'crono-fe-common/icons/Icon-Plus';
import ABTestingTemplates from '../abTestingTemplates';
import { useConfirmModal } from 'context/confirmModal';
import EditableTemplate from './editableTemplate';
import { Switch } from '@crono-one/design-system';
import { useAuth } from 'context/auth';
import {
  TypeOption,
  typeOptions,
} from 'pages/taskBoard/sequenceInstance/sequenceInstanceTaskComponent';
import { ReactComponent as TopArrowIcon } from 'crono-fe-common/icons/Top-Arrow.svg';
import { ReactComponent as BottomArrowIcon } from 'crono-fe-common/icons/Bottom-Arrow.svg';
import { TemplateCardsContainerTooltip } from 'pages/accountDrawer/linkedinView/templateCardsContainerTooltip';
import { Strategy } from 'crono-fe-common/types/strategy';
import TrashDeleteIcon from 'crono-fe-common/icons/Icon-Trash-Can';
import { cn } from 'utils/style-utils';
import ConditionalFlowButton from '../conditionalFlowButton';
import DelayButton from '../delayButton';
import { TemplateStep } from 'crono-fe-common/types/enums/templateStep';
import useDeleteStrategy from 'hooks/services/strategy/useDeleteStrategy';
import { useAnalytics } from 'context/analytics';
import { getDateFromScheduleTime, getError } from 'crono-fe-common/utils';
import { useConditionalSnackBar } from 'context/snackbar';
import GuidedTourSpotlight from 'components/GuidedTour';
import { IfInvitationIsAcceptedBanner } from 'pages/strategy/createStrategyWithAI/StrategyStructureEdit/strategyStructureStep/strategyStepBanners';
import AutoStaticBadge from './AutoStaticBadge';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import CronoVoiceVisualizer from 'pages/accountDrawer/linkedinView/cronoVoiceVisualizer';
import TemplateManagement from 'pages/templates/TemplateManagement';
import usePatchStrategyStep from 'hooks/services/strategy/usePatchStrategyStep';
import InformationTaskCard from 'pages/taskBoard/sequenceInstance/InformationTaskCard';
import LinkedinInvitationIcon from 'crono-fe-common/icons/Icon-Linkedin-Invitation';
import LinkedinIconInvoice from 'crono-fe-common/icons/Icon-Linkedin-Invoice';
import LinkedinViewProfileIcon from 'crono-fe-common/icons/Icon-Linkedin-View-Profile';
import LinkedinLikeReactionIcon from 'crono-fe-common/icons/Icon-Linkedin-Like-Reaction';
import LinkedinIconMessage from 'crono-fe-common/icons/Icon-Linkedin-Message';
import CallIcon from 'crono-fe-common/icons/Icon-Call';
import EmailIcon from 'crono-fe-common/icons/Icon-Email';
import styled from '@emotion/styled';
import type { HandleAllChangesStrategyStepTemplateType } from '../strategyDetailModal/strategyDetailModal';
import { ArchivedBadge } from 'components/Archive/ArchivedBadge';
import { IconTodo } from 'crono-fe-common/icons/IconTodo';
import { TodoSequenceTaskForm } from './editableTemplate/TodoSequenceTaskForm';
import PATH from 'crono-fe-common/constants/path';
import { useNavigate } from 'react-router-dom';
import { useStrategyNavigation } from '../useStrategyNavigation';
import ContactLocalTimeIndicator from './ContactLocalTimeIndicator';
import TimeOfDayPicker from 'components/TimeOfDayPicker';

export type ReplyThread = 'newThread' | 'existingThread';
type ChangeTypeOption = TypeOption & { disabled?: boolean };

interface IProps {
  step: StrategyStep;
  handleChange: (
    type: string,
    payload:
      | number
      | boolean
      | null
      | Date
      | Template
      | string
      | string[]
      | {
          type: TaskTodoType;
          subtype: TaskTodoSubtype | null;
        },
    index: number,
  ) => void;
  handleChangeConditionalStep: (
    type: string,
    payload: boolean | Date | null | Template,
    index: number,
  ) => void;
  handleChangeStrategyStepTemplate: (
    type: HandleAllChangesStrategyStepTemplateType,
    payload: {
      templateIndex: number;
      enabled?: boolean;
      strategyStepTemplateId: number;
    },
    index: number,
    conditional: boolean,
  ) => void;
  index: number;
  openTemplatePicker: () => void;
  openConditionalTemplatePicker: (
    type: SwitchToTask,
    abTesting?: boolean,
  ) => void;
  openABTestingTemplatePicker: (strategyStepTemplateId: number | null) => void;
  //In order to assign message or request to it
  firstLinkedin: boolean;
  //To know if it is possible to add a linkedin invitation
  beforeFirstLinkedin: boolean;
  //To know when to allow to change step type to linkedin non invitation
  indexInvitation: number;
  linkedinUntilAcceptedDays?: number;
  openAddStepModal: (index: number) => void;
  calculatedDate: string;
  calculatedDateConditional?: string;
  strategy: Strategy;
  patchConditionalStep: (data: StrategyConditionalStepPatch) => void;

  onSuccessEditTemplate: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;

  // templates tooltip
  templateComponent?: JSX.Element | null;
  closeTemplateTooltip?: () => void;
  showTemplateTab?: boolean;
  loadingAdd?: boolean;

  previewOnly?: boolean;
  isOnlyStepRemaining?: boolean;
  enableGuidedTours?: boolean;
  firstLinkedinMessageAfterInvitation?: boolean;
  stopAndJumpAlreadyPresent?: boolean;
}

const StepDetailComponent = ({
  step,
  handleChange,
  handleChangeConditionalStep,
  index,
  openTemplatePicker,
  openConditionalTemplatePicker,
  openABTestingTemplatePicker,
  firstLinkedin,
  beforeFirstLinkedin,
  indexInvitation,
  linkedinUntilAcceptedDays,
  openAddStepModal,
  calculatedDate,
  calculatedDateConditional,
  strategy,
  patchConditionalStep,
  handleChangeStrategyStepTemplate,
  onSuccessEditTemplate,
  templateComponent,
  closeTemplateTooltip,
  showTemplateTab,
  loadingAdd = false,
  previewOnly = false,
  enableGuidedTours = false,
  isOnlyStepRemaining = false,
  firstLinkedinMessageAfterInvitation = false,
  stopAndJumpAlreadyPresent,
}: IProps) => {
  const { id: strategyId } = useStrategyNavigation();

  const navigate = useNavigate();
  const handleChangeAbTesting = useCallback(
    (
      type: HandleAllChangesStrategyStepTemplateType,
      payload: {
        templateIndex: number;
        enabled?: boolean;
        strategyStepTemplateId: number;
      },
      index: number,
    ) => {
      handleChangeStrategyStepTemplate(type, payload, index, false);
    },
    [handleChangeStrategyStepTemplate],
  );

  const handleChangeAbTestingConditional = useCallback(
    (
      type: HandleAllChangesStrategyStepTemplateType,
      payload: {
        templateIndex: number;
        enabled?: boolean;
        strategyStepTemplateId: number;
      },
      index: number,
    ) => {
      handleChangeStrategyStepTemplate(type, payload, index, true);
    },
    [handleChangeStrategyStepTemplate],
  );

  const { hasSalesNav } = useAuth();

  const steps = strategy.steps;
  const { automaticLinkedinTaskExecutionLimit } = useAuth();
  const [isReplyThreadSubject, setIsReplyThreadSubject] = useState<ReplyThread>(
    step.replyToThread ? 'existingThread' : 'newThread',
  );

  // -----------------------------------------------

  const checkPreviousSteps = (
    step: StrategyStep,
    index: number,
    steps: StrategyStep[],
  ) => {
    // first step never has previous step
    if (step.order === 1) return false;

    // Filter the previous email steps. If there are any, then there must have been previous steps in that strategy
    const previousRelatedStep = steps
      .slice(0, index)
      .filter((s) => s.type === step.type);

    return previousRelatedStep.length > 0;
  };

  const findPreviousThreadSubject = (index: number, steps: StrategyStep[]) => {
    // Only the immediate previous email step is looked to get the subject
    const previousRelatedStep = steps
      .slice(0, index)
      .filter((s) => s.type === TaskTodoType.EMAIL && !s.replyToThread)
      .pop();

    if (previousRelatedStep) {
      if (previousRelatedStep.template) {
        return previousRelatedStep.template.subject;
      } else if (previousRelatedStep.strategyStepTemplates?.at(0)?.template) {
        return previousRelatedStep.strategyStepTemplates?.at(0)?.template
          ?.subject;
      }
    }
  };

  // Auto switch disabled logic if there are no AB testing templates and no singular step template
  const enableCheckSwitch = useMemo(() => {
    return (
      (step.type === TaskTodoType.LINKEDIN &&
        (step.subtype === TaskTodoSubtype.LinkedinInvitation ||
          step.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
          step.subtype === TaskTodoSubtype.LinkedinProfileView)) ||
      (step.strategyStepTemplates &&
        step.strategyStepTemplates?.length &&
        step.type !== TaskTodoType.CALL) ||
      (step.template && step.type !== TaskTodoType.CALL)
    );
  }, [
    step,
    step.strategyStepTemplates,
    step.template,
    step.type,
    step.subtype,
  ]);

  // These step types always let you pick a schedule time, whether or not
  // the step is automatic (unlike Invitation/LikeLastPost/ProfileView, which
  // only expose a time once automatic is turned on).
  const timeSelectorIndependentOfAutomatic =
    step.type === TaskTodoType.EMAIL ||
    step.type === TaskTodoType.CALL ||
    step.type === TaskTodoType.IN_MAIL ||
    step.type === TaskTodoType.GENERIC ||
    (step.type === TaskTodoType.LINKEDIN &&
      (step.subtype === TaskTodoSubtype.LinkedinMessage ||
        step.subtype === TaskTodoSubtype.LinkedinVoiceNote));

  const isLinkedinInvitation =
    step.type === TaskTodoType.LINKEDIN &&
    step.subtype === TaskTodoSubtype.LinkedinInvitation;

  const isEmail = useMemo(() => step.type === TaskTodoType.EMAIL, [step]);

  const [observerEditMode, setObserverEditMode] = useState<boolean>(false);

  const emailSubject = useMemo(
    () =>
      isReplyThreadSubject === 'newThread'
        ? step.template?.subject
        : findPreviousThreadSubject(index, steps),
    [isReplyThreadSubject, steps, step.template, index],
  );

  const emailThreadButton = useMemo(
    () => (
      <div className="flex-center email-thread">
        {isEmail && (
          <Select
            value={isReplyThreadSubject}
            startAdornment={<ReplyThreadIcon />}
            inputProps={{ IconComponent: () => null }}
            defaultValue="newThread"
            onChange={(e) => {
              setIsReplyThreadSubject(e.target.value as ReplyThread);
              handleChange(
                'replyToThread',
                e.target.value === 'existingThread',
                index,
              );
            }}
            disabled={observerEditMode}
            sx={{
              fontSize: '14px',
              minWidth: '120px',
              boxShadow: 'none',
              fontWeight: '500',
              borderRadius: '6px',
              border: `1px solid ${colors.grey44}`,
              gap: '4px',
              padding: '6px',
              '.MuiOutlinedInput-input': {
                padding: '0px',
              },
              '.MuiSelect-select': {
                padding: '0px !important',
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                color: colors.darkGreen,
                '&& svg path': {
                  stroke: colors.darkGreen,
                },
                '&& fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
            MenuProps={{
              style: { zIndex: 3501 },
              PaperProps: {
                sx: {
                  borderRadius: '8px',
                  paddingInline: '6px',
                  border: '1px solid #dadde9',
                  boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
                  '& .MuiMenuItem-root': {
                    fontWeight: '500',
                    fontSize: '12px',
                    lineHeight: '16px',
                    height: 'fit-content',
                    padding: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    borderRadius: '8px',
                    '&:hover': {
                      background: colors.primaryLight,
                      color: colors.primary,
                    },
                  },
                  '& .Mui-selected': {
                    color: colors.primary,
                    backgroundColor: 'transparent !important',
                  },
                },
              },
            }}
          >
            <MenuItem value={'newThread'}>New email thread</MenuItem>
            <MenuItem
              value={'existingThread'}
              disabled={!checkPreviousSteps(step, index, steps)}
            >
              Previous email thread
            </MenuItem>
          </Select>
        )}
      </div>
    ),
    [steps, step, index, isReplyThreadSubject, observerEditMode],
  );

  const [openTypeTooltip, setOpenTypeTooltip] = useState<boolean>(false);

  const [isGuidedTourTypeVisible, setIsGuidedTourTypeVisible] =
    useState<boolean>(false);

  const [signalGuidedTour, setSignalGuidedTour] = useState<boolean>(false);

  const readyToSignalGuidedTour = useRef<boolean>(false);

  useEffect(() => {
    if (!isGuidedTourTypeVisible) return;
    //When it is opened we get ready setting the signal to true
    if (openTypeTooltip) {
      readyToSignalGuidedTour.current = true;
      //When it becomes false and we were ready to signal, we set the signal to true
    } else if (readyToSignalGuidedTour.current) {
      setSignalGuidedTour(true);
    }
  }, [openTypeTooltip, isGuidedTourTypeVisible]);

  const stepsIncludeLinkedinWithConditional = useMemo(
    () =>
      steps.findIndex(
        (step) =>
          step.type === TaskTodoType.LINKEDIN && step.strategyStepConditional,
      ) !== -1,
    [steps],
  );

  const templatesButton = useMemo(
    () => (
      <div
        className="flex-center step-right-side-wrapper"
        style={{
          alignSelf:
            step.strategyStepConditional &&
            (step.template || step.strategyStepTemplates)
              ? 'flex-start'
              : undefined,
        }}
      >
        {/* Main add template button */}
        {!previewOnly ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              paddingLeft: step.strategyStepConditional ? '0px' : '20px',
            }}
          >
            <div
              className={`flex-center ${
                observerEditMode
                  ? ' disable-due-to-edit'
                  : step.template
                    ? 'selected-template gap-2'
                    : 'add-template'
              } ${step.strategyStepTemplates ? 'ab-testing' : ''}`}
            >
              {step.strategyStepTemplates ? (
                <>
                  <Typography fontSize={14} fontWeight={500} noWrap>
                    A/B testing
                  </Typography>
                  <span className="flex-center icon-wrapper">
                    <AddTemplateIconS
                      className={'ab-testing-step-component-button'}
                    />
                  </span>
                  <Typography fontSize={14} fontWeight={500} noWrap>
                    {step.strategyStepTemplates.length}
                  </Typography>
                </>
              ) : step.template ? (
                <>
                  <TemplateCardsContainerTooltip
                    title={templateComponent}
                    onClose={closeTemplateTooltip}
                    open={showTemplateTab}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                      }}
                    >
                      <span className="flex-center icon-wrapper">
                        <SelectedTemplateIcon
                          className={`${
                            observerEditMode
                              ? 'step-component-button-disabled'
                              : 'step-component-button'
                          }`}
                          color={colors.grey11}
                          onClick={openTemplatePicker}
                        />
                      </span>
                      <Typography
                        fontSize={'14px'}
                        fontWeight={500}
                        noWrap
                        onClick={openTemplatePicker}
                        maxWidth={'250px'}
                      >
                        {step.template.title}
                      </Typography>
                    </div>
                  </TemplateCardsContainerTooltip>

                  <span
                    onClick={() => {
                      handleChange('template', { id: 0 } as Template, index);
                    }}
                    style={{
                      cursor: observerEditMode ? 'default' : 'pointer',
                      display: 'flex',
                    }}
                    className="close-template-option"
                  >
                    <CloseMIcon color={colors.grey11} />
                  </span>

                  {step.template.archived && <ArchivedBadge />}
                </>
              ) : (
                <>
                  <TemplateCardsContainerTooltip
                    title={templateComponent}
                    onClose={closeTemplateTooltip}
                    open={showTemplateTab}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                      }}
                    >
                      <GuidedTourSpotlight
                        direction="top"
                        guidedTourProperty="createStrategyStep"
                        valueToSpotlightOn={'First'}
                        title=""
                        style={{
                          borderRadius: 999,
                          // padding: 4,
                          // margin: -4,
                        }}
                        disableForMultipleSpotlight={true}
                        disabled={!enableGuidedTours || previewOnly}
                      >
                        <span className="flex-center icon-wrapper">
                          <AddTemplateIconS
                            className={'step-component-button'}
                            onClick={() => {
                              openTemplatePicker();
                            }}
                          />
                        </span>
                      </GuidedTourSpotlight>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        noWrap
                        onClick={() => {
                          openTemplatePicker();
                        }}
                      >
                        Add template
                      </Typography>
                    </div>
                  </TemplateCardsContainerTooltip>
                </>
              )}
            </div>

            {observerEditMode && (
              <div
                className="badge-observable-mode"
                style={{ marginLeft: '10px' }}
              >
                <EditPencilIconS
                  color={colors.lightPinkHover}
                  className="pencil-icon"
                />
                Draft
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '18px 16px',
            }}
          >
            {step.template && (
              <>
                <div style={{ display: 'inline-flex', marginRight: '8px' }}>
                  <SelectedTemplateIcon />
                </div>

                <Typography
                  fontSize={14}
                  fontWeight={500}
                  lineHeight={'18px'}
                  color={colors.darkGreen}
                >
                  {step.template.title}
                </Typography>
              </>
            )}
          </div>
        )}
      </div>
    ),
    [
      observerEditMode,
      step.template,
      step.strategyStepTemplates,
      step.strategyStepConditional,
      handleChange,
      templateComponent,
      closeTemplateTooltip,
      openTemplatePicker,
      showTemplateTab,
    ],
  );

  const [templatePlaceholder, setTemplatePlaceholder] =
    useState<Template | null>(null);
  const [showCreateTemplateModal, setShowTemplateModal] =
    useState<boolean>(false);
  const createTemplatePlaceholder = useCallback(() => {
    const type = mapTaskTypeToTemplateType(step.type, step.subtype);
    if (!type) return;
    if (type == TemplateType.VOICE_NOTE) {
      setShowTemplateModal(true);
    } else {
      setTemplatePlaceholder({
        type: type,
        title: '',
        subject: '',
        content: '',

        //dummy data to make placeholder work
        id: -1,
        userId: -1,
        subscriptionId: -1,
        language: '',
        shared: false,
        step: TemplateStep.FIRST_TOUCH,
        useCount: 0,
        openCount: 0,
        answerCount: 0,
      });
    }
  }, [step]);

  const {
    mutate: deleteStrategy,
    error: deleteStrategyError,
    isSuccess: isDeleteStrategySuccess,
  } = useDeleteStrategy();

  const { openModal: openConfirmModal } = useConfirmModal();

  const analytics = useAnalytics();

  const { mutateAsync: patchStrategyStepAsync } = usePatchStrategyStep();

  useConditionalSnackBar([
    {
      condition: !!deleteStrategyError,
      message: getError(deleteStrategyError) ?? 'Error deleting sequence',
      severity: 'error',
    },
    {
      condition: isDeleteStrategySuccess,
      message: 'Sequence deleted',
      severity: 'success',
    },
  ]);

  const handleDeleteStrategy = () => {
    openConfirmModal({
      title: 'Are you sure you want to delete this sequence?',
      text: 'If you delete the last step of the sequence, the entire sequence will be deleted.',
      cancelText: 'Cancel',
      confirmText: 'Delete',
      cancelFunction: () => {
        return;
      },
      confirmFunction: () => {
        deleteStrategy(step.strategyId, {
          onSuccess: () => {
            setTimeout(() => {
              navigate(PATH.STRATEGIES);
            }, 500);
          },
        });
        if (analytics) {
          analytics.track('strategy-deleted', {
            strategyId: step.strategyId,
          });
        }
      },
    });
  };

  return (
    <StrategyStepWrapper>
      <div
        className="step-row"
        style={{
          paddingTop: index === 0 ? '12px' : '',
        }}
      >
        {showCreateTemplateModal && (
          <TemplateManagement
            close={() => setShowTemplateModal(false)}
            onCreated={(template) => {
              const handle = async () => {
                await patchStrategyStepAsync({
                  templateId: template.id,
                  id: step.id,
                  strategyId: step.strategyId,
                });
                onSuccessEditTemplate({
                  newInsertedTemplateId: template.id,
                  isAbTesting: false,
                });
              };
              handle();
            }}
          />
        )}

        {loadingAdd ? (
          <CircularProgress
            size={18}
            style={{
              marginInline: '21px',
            }}
          />
        ) : (
          <>
            {!previewOnly ? (
              <Tooltip placement="top" title="Add a new step" arrow>
                <div
                  className="add-step-container"
                  onClick={() => openAddStepModal(index)}
                >
                  <div className="step-plus">
                    <Typography fontSize={12} fontWeight={500}>
                      Add
                    </Typography>
                    <PlusSmallIcon color={colors.primary} />
                  </div>
                </div>
              </Tooltip>
            ) : (
              <div style={{ width: '72px' }} />
            )}
          </>
        )}

        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            justifyContent: step.strategyStepConditional
              ? 'space-around'
              : 'center',
            flex: 1,
          }}
          id={'delay' + index}
        >
          <GuidedTourSpotlight
            direction="top"
            guidedTourProperty="setDelayAndConditional"
            valueToSpotlightOn={null}
            title="Set delay between tasks"
            subtitle="Specify a wait time before the task is executed"
            //We want to show the guided tour on the second task only
            //This allows us to show this also in strategies that are not brand new, since only 1 would be displayed
            disabled={index !== 1 || previewOnly}
            style={{
              padding: 6,
              margin: -6,
              borderRadius: 8,
            }}
            positionOffset={{
              top: 26,
              left: 24,
            }}
            positionOffsetArrow={{
              left: 110,
            }}
          >
            <DelayButton
              disabled={previewOnly}
              delay={step.delay}
              increaseDelay={() => {
                if (previewOnly) return;
                handleChange('delay', 1, index);
              }}
              decreaseDelay={() => {
                if (previewOnly) return;
                handleChange('delay', -1, index);
              }}
              showLabel
              isFirstStep={index === 0}
            />
          </GuidedTourSpotlight>
          {step.strategyStepConditional && calculatedDateConditional && (
            <DelayButton
              disabled={previewOnly}
              delay={step.strategyStepConditional.daysAfterConditionNotAccepted}
              increaseDelay={() => handleChange('delay-conditional', 1, index)}
              decreaseDelay={() => handleChange('delay-conditional', -1, index)}
              showLabel={true}
            />
          )}
        </div>
      </div>

      <div className="step-row" style={previewOnly ? { gap: '40px' } : {}}>
        <div
          style={{
            height: '100%',
          }}
        >
          <div
            className="step-number-container"
            style={
              previewOnly
                ? {
                    minWidth: '40px',
                    width: 'auto',
                    margin: 0,
                  }
                : {}
            }
          >
            <span
              className={'step-number'}
              style={
                previewOnly ? { textAlign: 'center', lineHeight: '40px' } : {}
              }
            >
              {index + 1}
            </span>

            {!previewOnly && (
              <Tooltip
                title={'Delete step'}
                enterDelay={400}
                enterNextDelay={400}
                arrow
              >
                <div
                  className={'step-delete-icon-container'}
                  onClick={() => {
                    if (isOnlyStepRemaining) {
                      //nothing
                      handleDeleteStrategy();
                    } else {
                      handleChange('delete', null, index);
                    }
                  }}
                >
                  <TrashDeleteIcon />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div
          className={
            step?.strategyStepConditional
              ? 'step-content-conditional'
              : cn('step-content', {
                  'step-content-invitation': isLinkedinInvitation,
                })
          }
        >
          <div
            id={step.id.toString()}
            className="step-content-single-step"
            style={previewOnly ? { width: '95%' } : {}}
          >
            <div
              className={cn('step-content-top', {
                'step-content-no-border-bottom':
                  step?.strategyStepConditional ||
                  step?.template ||
                  (!step.template && step.strategyStepTemplates) ||
                  (!step.template &&
                    !step.strategyStepTemplates &&
                    !previewOnly &&
                    !templatePlaceholder) ||
                  templatePlaceholder,
              })}
              style={{
                borderBottom: step.template ? 'none' : '',
                ...(step?.strategyStepConditional ||
                (isLinkedinInvitation && stepsIncludeLinkedinWithConditional)
                  ? {
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'fit-content',
                    }
                  : {}),
              }}
            >
              {step.strategyStepConditional && (
                <IfInvitationIsAcceptedBanner
                  style={{
                    width: '100%',
                    margin: 0,
                  }}
                />
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div className="flex-center step-left-side-wrapper">
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    {renderActivityIcon(step)}

                    {!previewOnly ? (
                      <GuidedTourSpotlight
                        direction="bottom"
                        guidedTourProperty="createStrategyStep"
                        valueToSpotlightOn={null}
                        title="Click here to change the task type"
                        subtitle="You can create Email, Call and all types of LinkedIn tasks"
                        style={{
                          borderRadius: 8,
                          padding: 4,
                          margin: -4,
                        }}
                        extractIsVisible={(isVisible) =>
                          setIsGuidedTourTypeVisible(isVisible)
                        }
                        disableAutoPatch={true}
                        signalPatch={signalGuidedTour}
                        disabled={!enableGuidedTours || previewOnly}
                      >
                        <div
                          className="step-type-option-dropdown"
                          style={
                            step?.strategyStepConditional
                              ? { width: 'max-content' }
                              : {}
                          }
                        >
                          <StepType
                            beforeFirstLinkedin={beforeFirstLinkedin}
                            firstLinkedin={firstLinkedin}
                            handleChange={handleChange}
                            index={index}
                            indexInvitation={indexInvitation}
                            task={step}
                            forConditionals={!!step?.strategyStepConditional}
                            extractIsOpenTypeTooltip={(isOpen: boolean) =>
                              setOpenTypeTooltip(isOpen)
                            }
                          />
                        </div>
                      </GuidedTourSpotlight>
                    ) : (
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        lineHeight={'18px'}
                        color={colors.black}
                      >
                        {step.type !== TaskTodoType.LINKEDIN ? (
                          <>{step.type}</>
                        ) : (
                          <>
                            {/* linkedin types always have a subtype */}
                            {dispartPascalCase(step.subtype || 'Linkedin')}
                          </>
                        )}
                      </Typography>
                    )}
                  </div>

                  {!(
                    step.type === TaskTodoType.LINKEDIN &&
                    (step.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
                      step.subtype === TaskTodoSubtype.LinkedinProfileView)
                  ) &&
                    previewOnly &&
                    step.automatic && (
                      <div
                        style={{
                          width: '45px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: colors.lightBlue,
                        }}
                      >
                        <Typography
                          fontSize={12}
                          lineHeight={'24px'}
                          fontWeight={500}
                          color={colors.blueHover}
                          textAlign={'center'}
                        >
                          Auto
                        </Typography>
                      </div>
                    )}

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {step.type !== TaskTodoType.CALL && (
                      <>
                        {step.type === TaskTodoType.LINKEDIN &&
                        (step.subtype ===
                          TaskTodoSubtype.LinkedinLikeLastPost ||
                          step.subtype ===
                            TaskTodoSubtype.LinkedinProfileView) ? (
                          <AutoStaticBadge />
                        ) : (
                          !previewOnly && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {(step.type === TaskTodoType.EMAIL ||
                                (step.type === TaskTodoType.LINKEDIN &&
                                  (!step.subtype ||
                                    step.subtype ===
                                      TaskTodoSubtype.LinkedinVoiceNote ||
                                    step.subtype ===
                                      TaskTodoSubtype.LinkedinMessage ||
                                    step.subtype ===
                                      TaskTodoSubtype.LinkedinInvitation)) ||
                                step.type === TaskTodoType.IN_MAIL) && (
                                <GuidedTourSpotlight
                                  guidedTourProperty="createStrategyStep"
                                  valueToSpotlightOn="Second"
                                  direction="top"
                                  title="Automate task execution"
                                  subtitle="If a task has a template attached or if it is an invitation, you can automate its execution"
                                  style={{
                                    height: 50,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    paddingLeft: 8,
                                    marginLeft: -8,
                                    borderRadius: 10,
                                  }}
                                  disabled={
                                    !enableGuidedTours ||
                                    !enableCheckSwitch ||
                                    showTemplateTab ||
                                    previewOnly
                                  }
                                >
                                  <Tooltip
                                    arrow
                                    title={
                                      step.type === TaskTodoType.LINKEDIN &&
                                      step.subtype ===
                                        TaskTodoSubtype.LinkedinInvitation
                                        ? `You can automate up to ${automaticLinkedinTaskExecutionLimit} LinkedIn tasks daily. Tasks that exceed the limit will be postponed to the next day`
                                        : step.template ||
                                            step.strategyStepTemplates
                                          ? step.type === TaskTodoType.LINKEDIN
                                            ? `You can automate up to ${automaticLinkedinTaskExecutionLimit} LinkedIn tasks daily. Tasks that exceed the limit will be postponed to the next day`
                                            : 'Automate this step'
                                          : 'Toggle Auto. Can be turned on and off. Cannot be turned on if no template is selected'
                                    }
                                    enterDelay={400}
                                    enterNextDelay={400}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Typography
                                        fontSize={14}
                                        fontWeight={500}
                                        color={colors.grey11}
                                      >
                                        Auto
                                      </Typography>

                                      <Switch
                                        checked={step.automatic}
                                        onCheckedChange={(checked) =>
                                          handleChange(
                                            'automatic',
                                            checked,
                                            index,
                                          )
                                        }
                                        disabled={!enableCheckSwitch}
                                        className="mx-2"
                                      />
                                    </div>
                                  </Tooltip>
                                </GuidedTourSpotlight>
                              )}
                            </div>
                          )
                        )}

                        {previewOnly && step.automatic && <></>}
                      </>
                    )}

                    {(step.automatic || timeSelectorIndependentOfAutomatic) && (
                      <TimeOfDayPicker
                        value={getDateFromScheduleTime(step.scheduleTime)}
                        onChange={(newDate) =>
                          handleChange('scheduleTime', newDate, index)
                        }
                        disabled={
                          !timeSelectorIndependentOfAutomatic &&
                          (!enableCheckSwitch || !step.automatic)
                        }
                        className="min-w-24 max-w-24 w-24"
                      />
                    )}

                    {strategy.createOnProspectTimezone &&
                      (step.automatic ||
                        timeSelectorIndependentOfAutomatic ||
                        isLinkedinInvitation) && <ContactLocalTimeIndicator />}
                  </div>
                </div>

                {!step?.strategyStepConditional &&
                  !(
                    step.type === TaskTodoType.LINKEDIN &&
                    (step.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
                      step.subtype === TaskTodoSubtype.LinkedinProfileView)
                  ) &&
                  step.type !== TaskTodoType.GENERIC && (
                    <>
                      <div className="vertical-divider" />
                      {templatesButton}
                    </>
                  )}
              </div>
            </div>

            {step.type === TaskTodoType.GENERIC && (
              <div className="step-content-bottom">
                <TodoSequenceTaskForm
                  key={step.id}
                  step={step}
                  onSubjectChange={(subject) =>
                    handleChange('subject', subject, index)
                  }
                  onDescriptionChange={(description) =>
                    handleChange('description', description, index)
                  }
                />
              </div>
            )}

            {!(
              step.type === TaskTodoType.LINKEDIN &&
              (step.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
                step.subtype === TaskTodoSubtype.LinkedinProfileView)
            ) &&
              step.type !== TaskTodoType.GENERIC &&
              !step.template &&
              !step.strategyStepTemplates &&
              !previewOnly &&
              !templatePlaceholder && (
                <GuidedTourSpotlight
                  direction="top"
                  guidedTourProperty="createStrategyStep"
                  valueToSpotlightOn={'First'}
                  title="Create a template from scratch or add from library"
                  subtitle="Click on the field to start creating a template for this sequence task. You can also select template from your library  by clicking on “Add template” button"
                  style={{
                    borderRadius: 8,
                    padding: 4,
                    margin: -4,
                  }}
                  positionOffset={{
                    top: -46,
                  }}
                  disabled={!enableGuidedTours || previewOnly}
                >
                  <div
                    onClick={createTemplatePlaceholder}
                    className={cn(
                      'step-click-to-create step-content-no-border-top',
                      {
                        'step-click-to-create--conditional':
                          step?.strategyStepConditional,
                        'step-click-to-create--invitation':
                          isLinkedinInvitation,
                      },
                    )}
                  >
                    <Typography
                      lineHeight={'28px'}
                      fontSize={'14px'}
                      color={colors.grey2}
                    >
                      No template selected. Click here to create a new template.
                    </Typography>
                  </div>
                </GuidedTourSpotlight>
              )}

            {templatePlaceholder && (
              <div
                className={cn(
                  'template-creation-wrapper step-content-no-border-top step-content-bottom',
                  {
                    'step-click-to-create--conditional':
                      step.strategyStepConditional,
                    'step-click-to-create--invitation': isLinkedinInvitation,
                  },
                )}
              >
                <EditableTemplate
                  removeEmptyTemplate={() => setTemplatePlaceholder(null)}
                  createNewTemplate={true}
                  emailThreadButton={undefined}
                  subject={isEmail ? '' : undefined}
                  enableSubjectMutation={isReplyThreadSubject === 'newThread'}
                  stepId={step.id}
                  template={templatePlaceholder}
                  setObserverEditMode={(value) => setObserverEditMode(value)}
                  onSuccess={onSuccessEditTemplate}
                  previewOnly={previewOnly}
                  strategyId={strategyId!}
                />
              </div>
            )}

            {((!step.template && step.strategyStepTemplates) ||
              step.strategyStepConditional ||
              step.template ||
              (step.type === TaskTodoType.LINKEDIN &&
                step.subtype === TaskTodoSubtype.LinkedinInvitation &&
                step.template)) && (
              <div className="step-content-bottom">
                {(step.type === TaskTodoType.LINKEDIN && step.subtype) ===
                  TaskTodoSubtype.LinkedinInvitation &&
                  !!step.templateId &&
                  !hasSalesNav && (
                    <InformationTaskCard
                      text={
                        'Warning: LinkedIn standard licenses have a limit on personalised invitations. If you add a Note but you do not have a LinkedIn Premium License, your tasks might be blocked by LinkedIn.'
                      }
                      type={'warning'}
                    />
                  )}

                {/*Switcher to end strategy if invitation accepted */}
                {step.type === TaskTodoType.LINKEDIN &&
                  step.subtype === TaskTodoSubtype.LinkedinInvitation &&
                  (step.template || step.strategyStepTemplates) && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        alignSelf: 'flex-end',
                      }}
                    >
                      <Typography
                        fontSize={12}
                        lineHeight={'16px'}
                        color={colors.grey11}
                      >
                        Stop sequence when this note is replied
                      </Typography>

                      <Switch
                        disabled={previewOnly}
                        checked={
                          step.stopSequenceInstanceIfInvitationReplied ?? false
                        }
                        onCheckedChange={(checked) => {
                          handleChange(
                            'stopSequenceInstanceIfInvitationReplied',
                            checked,
                            index,
                          );
                        }}
                        className="mx-2"
                      />
                    </div>
                  )}
                {/* Single template dropdown and A/B testing templates*/}
                {!step.template && step.strategyStepTemplates ? (
                  <>
                    {step.strategyStepTemplates?.map(
                      (strategyStepTemplate, _index) => {
                        return (
                          <ABTestingTemplates
                            //This is a trick to force the re-render of the component when the template is changed
                            key={`${strategyStepTemplate.templateId}${strategyStepTemplate.template?.content.length}${_index}`}
                            strategyStepTemplate={strategyStepTemplate}
                            stepOrConditionalStep={step}
                            index={index}
                            currentIndex={_index}
                            stepId={step.id}
                            handleChange={handleChangeAbTesting}
                            strategyStepTemplates={step.strategyStepTemplates!}
                            onSuccessEditTemplate={onSuccessEditTemplate}
                            EmailThreadButton={emailThreadButton}
                            previewOnly={previewOnly}
                          />
                        );
                      },
                    )}

                    {!previewOnly && (
                      <>
                        <div className="horizontal-divider" />

                        <TemplateCardsContainerTooltip
                          title={templateComponent}
                          onClose={closeTemplateTooltip}
                          open={showTemplateTab}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                        >
                          <div
                            className={
                              !previewOnly
                                ? 'ab-testing-wrapper'
                                : 'ab-testing-wrapper-preview'
                            }
                          >
                            <PlusIcon />

                            <Typography
                              fontSize={'14px'}
                              fontWeight={500}
                              onClick={() => {
                                if (previewOnly) return;

                                openABTestingTemplatePicker(
                                  step.strategyStepTemplates?.[0].id ?? null,
                                );
                              }}
                            >
                              Add template
                            </Typography>
                          </div>
                        </TemplateCardsContainerTooltip>
                      </>
                    )}
                  </>
                ) : step?.strategyStepConditional || step?.template ? (
                  <>
                    {step?.strategyStepConditional && (
                      <>
                        <div className="vertical-divider" />
                        {templatesButton}
                      </>
                    )}

                    {step?.template &&
                      (step.template.type === TemplateType.VOICE_NOTE ? (
                        <CronoVoiceVisualizer
                          duration={step.template.recordingDuration}
                          templateId={step.template.id}
                          allowToRemove={false}
                          size={
                            step?.strategyStepConditional ? 'small' : 'medium'
                          }
                        />
                      ) : (
                        <>
                          <EditableTemplate
                            emailThreadButton={
                              isEmail ? emailThreadButton : undefined
                            }
                            subject={
                              isEmail ? emailSubject : step.template?.subject
                            }
                            enableSubjectMutation={
                              isReplyThreadSubject === 'newThread'
                            }
                            stepId={step.id}
                            template={step.template}
                            setObserverEditMode={(value) =>
                              setObserverEditMode(value)
                            }
                            onSuccess={onSuccessEditTemplate}
                            previewOnly={previewOnly}
                            onCcChange={(emails) =>
                              handleChange('ccAddresses', emails, index)
                            }
                            ccAddresses={step.ccAddresses}
                            strategyId={strategyId!}
                          />

                          {!previewOnly &&
                            step.subtype !==
                              TaskTodoSubtype.LinkedinVoiceNote && (
                              <>
                                <div className="horizontal-divider" />
                                <div
                                  onClick={() => {
                                    openABTestingTemplatePicker(null);
                                  }}
                                  className={'ab-testing-wrapper'}
                                >
                                  <PlusIcon />

                                  <Typography
                                    fontSize={'14px'}
                                    fontWeight={500}
                                  >
                                    Add A/B testing
                                  </Typography>
                                </div>
                              </>
                            )}
                        </>
                      ))}
                  </>
                ) : null}
              </div>
            )}
            {step.type === TaskTodoType.LINKEDIN &&
              (step.subtype === TaskTodoSubtype.LinkedinProfileView ||
                step.subtype === TaskTodoSubtype.LinkedinLikeLastPost) && (
                <div
                  className="step-content-bottom"
                  style={{
                    background: colors.grey6,
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    fontSize={14}
                    lineHeight={'24px'}
                    color={colors.grey1}
                    flexWrap={'wrap'}
                  >
                    {step.subtype === TaskTodoSubtype.LinkedinLikeLastPost
                      ? 'The latest LinkedIn post (repost excluded) will be automatically liked, if published in the last 30 days. If not present, the task will be skipped.'
                      : 'The LinkedIn profile will be automatically viewed. If the LinkedIn URL is missing, the task will be skipped.'}
                  </Typography>
                </div>
              )}
          </div>
          {/* Conditional step */}
          {step?.strategyStepConditional ? (
            <div className="conditional-step-wrapper">
              <ConditionalStepDetailComponent
                step={step}
                patchConditionalStep={patchConditionalStep}
                conditionalStep={step.strategyStepConditional}
                handleChange={handleChangeConditionalStep}
                index={index}
                openConditionalTemplatePicker={openConditionalTemplatePicker}
                previousThreadSubject={findPreviousThreadSubject(index, steps)}
                onSuccessEditTemplate={onSuccessEditTemplate}
                previewOnly={previewOnly}
                firstLinkedinMessageAfterInvitation={
                  firstLinkedinMessageAfterInvitation
                }
                stopAndJumpAlreadyPresent={stopAndJumpAlreadyPresent}
                handleChangeAbTesting={handleChangeAbTestingConditional}
                strategyId={strategyId!}
              />
            </div>
          ) : null}
        </div>
      </div>

      {isLinkedinInvitation && stepsIncludeLinkedinWithConditional && (
        <div className="step-row">
          <div
            className="step-content"
            style={{
              marginLeft: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <GuidedTourSpotlight
              direction="top"
              guidedTourProperty="setDelayAndConditional"
              valueToSpotlightOn={'First'}
              title="You can chose waiting time or remove condition here"
              subtitle="If invitation is accepted within your chosen days, LinkedIn tasks will be sent. If not, it will be skipped."
              style={{
                padding: 6,
                margin: -6,
                borderRadius: 8,
                width: 'calc(100% + 16px)',
              }}
              positionOffset={{
                top: -8,
                left: 300,
              }}
              positionOffsetArrow={{
                left: 110,
              }}
              disabled={previewOnly}
            >
              <ConditionalFlowButton
                id={`${step.id}-conditional-button`}
                delay={linkedinUntilAcceptedDays ?? 0}
                increaseDelay={() => handleChange('untilAccepted', 1, index)}
                decreaseDelay={() => handleChange('untilAccepted', -1, index)}
                previewOnly={previewOnly}
              />
            </GuidedTourSpotlight>
          </div>
        </div>
      )}
    </StrategyStepWrapper>
  );
};

interface IStepType {
  task: StrategyStep;
  index: number;
  indexInvitation: number;
  firstLinkedin: boolean;
  beforeFirstLinkedin: boolean;
  forConditionals: boolean;
  handleChange: (
    type: string,
    payload: {
      type: TaskTodoType;
      subtype: TaskTodoSubtype | null;
    },
    index: number,
  ) => void;
  extractIsOpenTypeTooltip?: (value: boolean) => void;
}

const RenderActivityIconWrapper = styled.div`
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;

    &--call {
      background-color: ${colors.callScheduled};
    }

    &--email {
      background-color: ${colors.inactive};
    }

    &--linkedin {
      background-color: ${colors.blueHover};
    }

    &--linkedin-light {
      background-color: ${colors.prospecting};
    }

    &--inmail {
      background-color: ${colors.purple};
    }

    &--todo {
      background-color: ${colors.lightPink};
    }
  }
`;

export const renderActivityIcon = (step: {
  type: TaskTodoType;
  subtype?: TaskTodoSubtype | null;
}) => {
  let modifier = 'call';

  let IconComponent;
  switch (step.type) {
    case TaskTodoType.CALL:
      modifier = 'call';
      IconComponent = <CallIcon color={colors.white} />;
      break;
    case TaskTodoType.EMAIL:
      modifier = 'email';
      IconComponent = <EmailIcon color={colors.white} />;
      break;
    case TaskTodoType.LINKEDIN:
      if (step.subtype === TaskTodoSubtype.LinkedinInvitation) {
        modifier = 'linkedin';
        IconComponent = <LinkedinInvitationIcon color={colors.white} />;
      } else if (step.subtype === TaskTodoSubtype.LinkedinVoiceNote) {
        modifier = 'linkedin';
        IconComponent = <LinkedinIconInvoice color={colors.white} size={16} />;
      } else if (step.subtype === TaskTodoSubtype.LinkedinProfileView) {
        modifier = 'linkedin-light';
        IconComponent = <LinkedinViewProfileIcon color={colors.white} />;
      } else if (step.subtype === TaskTodoSubtype.LinkedinLikeLastPost) {
        modifier = 'linkedin-light';
        IconComponent = <LinkedinLikeReactionIcon color={colors.white} />;
      } else {
        modifier = 'linkedin';
        IconComponent = <LinkedinIconMessage color={colors.white} />;
      }
      break;
    case TaskTodoType.IN_MAIL:
      modifier = 'inmail';
      IconComponent = <LinkedinIconInMailCompassDark />;
      break;
    case TaskTodoType.GENERIC:
      modifier = 'todo';
      IconComponent = <IconTodo color={colors.white} />;
      break;
  }

  return (
    <RenderActivityIconWrapper>
      <div className={`icon icon--${modifier}`}>{IconComponent}</div>
    </RenderActivityIconWrapper>
  );
};

export const StepType = ({
  task,
  index,
  indexInvitation,
  firstLinkedin,
  beforeFirstLinkedin,
  handleChange,
  forConditionals,
  extractIsOpenTypeTooltip,
}: IStepType) => {
  const [openTypeTooltip, setOpenTypeTooltip] = useState<boolean>(false);

  useEffect(() => {
    if (!extractIsOpenTypeTooltip) return;
    extractIsOpenTypeTooltip(openTypeTooltip);
  }, [openTypeTooltip]);

  const { openModal: openConfirmModal } = useConfirmModal();

  const showABTestingDisclaimer = () => {
    openConfirmModal({
      title: 'You have added AB testing templates to this step',
      text: 'If you modify the step type, all statistics related to those templates will be lost and you will not be able to recover them',
      cancelText: 'Cancel',
      confirmText: 'Modify',
      confirmFunction: () => {
        setOpenTypeTooltip((prev) => !prev);
      },
      cancelFunction: () => {
        return;
      },
    });
  };

  const type = parseTaskTypeToString(
    task.type,
    task.subtype ?? null,
    forConditionals,
  );

  return (
    <ClickAwayListener onClickAway={() => setOpenTypeTooltip(false)}>
      <div style={{ width: '100%' }}>
        <CustomOptionsTooltip
          maxHeight={220}
          width={200}
          left={1}
          title={
            <StepTypeOptionsWrapper>
              <PossibleSteps
                beforeFirstLinkedin={beforeFirstLinkedin}
                firstLinkedin={firstLinkedin}
                indexInvitation={indexInvitation}
                handleChange={handleChange}
                currentTask={task}
                index={index}
                setOpenTypeTooltip={setOpenTypeTooltip}
              />
            </StepTypeOptionsWrapper>
          }
          disableFocusListener
          disableHoverListener
          disableTouchListener
          open={openTypeTooltip}
          onClose={() => setOpenTypeTooltip(false)}
          borderRadius={'16px'}
          sx={{
            zIndex: '10001 !important',
          }}
        >
          <div
            className="step-type-option-container"
            onClick={() => {
              if (task.strategyStepTemplates) {
                showABTestingDisclaimer();
                return;
              }
              setOpenTypeTooltip((prev) => !prev);
            }}
          >
            <div className="step-type-option">
              <Typography
                fontSize={'12px'}
                fontWeight={500}
                paddingLeft={'12px'}
              >
                {type}
              </Typography>
            </div>
            {openTypeTooltip ? <TopArrowIcon /> : <BottomArrowIcon />}
          </div>
        </CustomOptionsTooltip>
      </div>
    </ClickAwayListener>
  );
};

const PossibleSteps = ({
  currentTask,
  index,
  indexInvitation,
  firstLinkedin,
  beforeFirstLinkedin,
  handleChange,
  setOpenTypeTooltip,
}: {
  currentTask: StrategyStep;
  index: number;
  indexInvitation: number;
  firstLinkedin: boolean;
  beforeFirstLinkedin: boolean;
  handleChange: (
    type: string,
    payload: {
      type: TaskTodoType;
      subtype: TaskTodoSubtype | null;
    },
    index: number,
  ) => void;
  setOpenTypeTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { hasSalesNav, showUpgradeBadges } = useAuth();
  const optionsToUse = (typeOptions as ChangeTypeOption[])
    .filter((option) => {
      if (
        currentTask.type !== TaskTodoType.IN_MAIL &&
        option.type === TaskTodoType.IN_MAIL &&
        !hasSalesNav
      ) {
        return false;
      }
      if (showUpgradeBadges) {
        if (
          option.type === TaskTodoType.CALL ||
          option.type === TaskTodoType.EMAIL
        ) {
          return false;
        }
      }
      return (
        option.type !== currentTask.type ||
        (currentTask.subtype &&
          option.subtype &&
          option.subtype !== currentTask.subtype)
      );
    })
    .map((option) => {
      const newOption = option;
      if (option.subtype === TaskTodoSubtype.LinkedinInvitation) {
        const allowChangeToInvitation =
          indexInvitation === -1 && (beforeFirstLinkedin || firstLinkedin);
        //  &&
        // (beforeFirstLinkedin ||
        //   (firstLinkedin &&
        //     currentTask.subtype !== TaskTodoSubtype.LinkedinInvitation));

        if (allowChangeToInvitation) {
          newOption.disabled = false;
        } else {
          newOption.disabled = true;
        }
      }

      if (
        option.type === TaskTodoType.LINKEDIN &&
        (option.subtype === TaskTodoSubtype.LinkedinMessage ||
          option.subtype === TaskTodoSubtype.LinkedinVoiceNote)
      ) {
        const allowChangeToLinkedinNonInvitation =
          // firstLinkedin || indexInvitation === -1 ||
          index >= indexInvitation;
        if (allowChangeToLinkedinNonInvitation) {
          newOption.disabled = false;
        } else {
          newOption.disabled = true;
        }
      }
      return newOption;
    });
  return (
    <>
      {optionsToUse.map((option, indexOption) => {
        return (
          <Tooltip
            arrow
            placement="top"
            enterDelay={600}
            enterNextDelay={600}
            PopperProps={{ sx: { zIndex: '10002 !important' } }}
            title={
              option.disabled
                ? option.subtype === TaskTodoSubtype.LinkedinInvitation
                  ? 'You can have only one invitation, and it should be placed before other LinkedIn steps'
                  : 'If invitation is present you can have linkedin steps only after it'
                : null
            }
            key={indexOption}
          >
            <div key={indexOption}>
              <Button
                className={cn('option-button', {
                  'option-disabled': option.disabled,
                })}
                color="primary"
                fullWidth
                endIcon={option.icon}
                onClick={() => {
                  if (!option.disabled) {
                    handleChange(
                      'type',
                      { type: option.type, subtype: option.subtype },
                      index,
                    );
                    setOpenTypeTooltip(false);
                  }
                }}
              >
                {option.text}
              </Button>
            </div>
          </Tooltip>
        );
      })}
    </>
  );
};

export default StepDetailComponent;
